import {Box, Occupancy} from "@variocube/app-api";
import {
    Avatar,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSort,
    typography,
    useBoxes,
    useOccupancies,
    usePlatform
} from "@variocube/cube-ui";
import * as React from "react";
import {useState} from "react";
import {Delivery, getOccupancyDeliveries} from "./types";
import {naturalCompare} from "@variocube/app-common";
import {useLocalization} from "../i18n";

interface BoxWithOccupancy {
    box: Box;
    occupancies: Occupancy[];
}

function sortColumn(b: BoxWithOccupancy, column: string): string {
    switch (column) {
        case 'box':
            return b.box.number;
        case 'occupancy':
            return b.occupancies
                .flatMap(occupancy => getOccupancyDeliveries(occupancy) || [])
                .map((d: Delivery) => d.order?.description || d.delivery.deliveryId || '').join();
        case 'recipient':
            return b.occupancies
                .flatMap(occupancy => getOccupancyDeliveries(occupancy) || [])
                .map((d) => d.recipients.map((r) => r.recipientName).join()).join();
        default:
            throw new Error(`There is no sort configuration for column ${column}`);
    }
}

function compareBox(a: BoxWithOccupancy, b: BoxWithOccupancy, sort: TableSort): number {
    const aSortValue = sortColumn(a, sort.column);
    const bSortValue = sortColumn(b, sort.column);
    let res;
    if (sort.column == 'box') {
        res = naturalCompare(aSortValue, bSortValue);
    } else {
        res = aSortValue.localeCompare(bSortValue);
    }
    if (sort.order == 'desc') {
        return res * -1;
    }
    return res;
}

export function BoxTable() {
    const [sort, setSort] = useState<TableSort>({
        column: 'box',
        order: 'asc'
    });

    const platform = usePlatform();
    const boxes = useBoxes();
    const occupancies = useOccupancies();

    const {t} = useLocalization();
    const getOccupancies = (box: Box) => (occupancies || []).filter(occupancy => occupancy.boxNumber == box.number);
    const boxesWithOccupancy = boxes ?
        boxes.map((b) => ({
            box: b, occupancies: getOccupancies(b)
        })) : [];
    const sorted = boxesWithOccupancy.sort((a, b) => compareBox(a, b, sort));

    async function handleOpenBox(box: Box) {
        await platform.openBoxLock(box.number);
    }

    return (
        <Table sort={sort} onSort={setSort}>
            <TableHead>
                <TableRow>
                    <TableCell column="box">{t("Box")}</TableCell>
                    <TableCell column="occupancy">{t("Occupancy")}</TableCell>
                    <TableCell column="recipient">{t("Recipient")}</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sorted.map(b => (
                    <BoxRow key={b.box.number} box={b.box} occupancies={b.occupancies} onOpenBox={handleOpenBox}/>
                ))}
            </TableBody>
        </Table>
    );

}


interface BoxRowProps {
    box: Box;
    occupancies: Occupancy[];
    onOpenBox: (box: Box) => void;
}

function BoxRow(props: BoxRowProps) {
    const {t} = useLocalization();
    const {box, occupancies, onOpenBox} = props;
    const deliveries = occupancies.flatMap(occupancy => getOccupancyDeliveries(occupancy) || []);
    return (
        <TableRow>
            <TableCell>
                <Avatar size={1.5}>{box.number}</Avatar>
            </TableCell>
            <TableCell>
                {deliveries.map(delivery => (
                    <DeliveryDisplay key={delivery.delivery.deliveryId} delivery={delivery}/>
                ))}
            </TableCell>
            <TableCell>
                {deliveries.map(delivery => (
                    <RecipientDisplay key={delivery.delivery.deliveryId} delivery={delivery}/>
                ))}
            </TableCell>
            <TableCell className={typography.alignRight}>
                <PushButton primary onClick={() => onOpenBox(box)}>
                    {t("OpenBox")}
                </PushButton>
            </TableCell>
        </TableRow>
    );
}

function DeliveryDisplay({delivery}: { delivery: Delivery }) {
    if (delivery.order) {
        return <div>{delivery.order.description}</div>;
    }
    return <div>{delivery.delivery.deliveryId}</div>;
}

function RecipientDisplay({delivery}: { delivery: Delivery }) {
    return <div>{delivery.recipients.map(recipient => <div
        key={recipient.recipientName}>{recipient.recipientName}</div>)}</div>;
}